<template>
  <div class="holland-code-hero">
    <div class="hero-content">
      <!-- Holland Code大标题 -->
      <h2 class="hero-title">您的Holland Code</h2>
      <p class="hero-subtitle">职业兴趣类型代码</p>
      
      <!-- Holland Code 3位字母展示 -->
      <div class="code-display">
        <span
          v-for="(letter, index) in codeLetters"
          :key="index"
          class="code-letter"
          :style="{ color: getTypeColor(letter) }"
        >
          {{ letter }}
        </span>
      </div>
      
      <!-- 类型名称展示 -->
      <div class="type-names">
        <div class="type-badge primary-badge" :style="{ background: primaryGradient }">
          <span class="type-emoji">{{ primaryTypeInfo.emoji }}</span>
          <span class="type-name">{{ primaryTypeInfo.name }}</span>
        </div>
        <span class="plus-sign">+</span>
        <div class="type-badge secondary-badge" :style="{ background: secondaryGradient }">
          <span class="type-emoji">{{ secondaryTypeInfo.emoji }}</span>
          <span class="type-name">{{ secondaryTypeInfo.name }}</span>
        </div>
        <span class="plus-sign">+</span>
        <div class="type-badge tertiary-badge" :style="{ background: tertiaryGradient }">
          <span class="type-emoji">{{ tertiaryTypeInfo.emoji }}</span>
          <span class="type-name">{{ tertiaryTypeInfo.name }}</span>
        </div>
      </div>
      
      <!-- 主类型简短描述 -->
      <p class="primary-description">
        {{ primaryTypeInfo.description }}
      </p>
      
      <!-- 测试完成时间 -->
      <div class="test-info">
        <span>测试完成于 {{ formattedDate }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { HOLLAND_TYPES } from '@/data/hollandTypes'

export default {
  name: 'HollandCodeDisplay',
  props: {
    hollandCode: {
      type: String,
      required: true,
      validator: (value) => value.length === 3
    },
    timestamp: {
      type: String,
      default: () => new Date().toISOString()
    }
  },
  computed: {
    codeLetters() {
      return this.hollandCode.split('')
    },
    primaryTypeInfo() {
      return HOLLAND_TYPES[this.codeLetters[0]] || {}
    },
    secondaryTypeInfo() {
      return HOLLAND_TYPES[this.codeLetters[1]] || {}
    },
    tertiaryTypeInfo() {
      return HOLLAND_TYPES[this.codeLetters[2]] || {}
    },
    primaryGradient() {
      return this.primaryTypeInfo.gradient || '#gray'
    },
    secondaryGradient() {
      return this.secondaryTypeInfo.gradient || '#gray'
    },
    tertiaryGradient() {
      return this.tertiaryTypeInfo.gradient || '#gray'
    },
    formattedDate() {
      const date = new Date(this.timestamp)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  },
  methods: {
    getTypeColor(letter) {
      return HOLLAND_TYPES[letter]?.color || '#gray'
    }
  }
}
</script>

<style scoped>
.holland-code-hero {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 24px;
  padding: 3rem 2rem;
  text-align: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

/* 装饰性背景 */
.holland-code-hero::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -20%;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  border-radius: 50%;
}

.holland-code-hero::after {
  content: '';
  position: absolute;
  bottom: -50%;
  left: -20%;
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  border-radius: 50%;
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 2rem;
  font-weight: 800;
  color: white;
  margin-bottom: 0.5rem;
}

.hero-subtitle {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
}

/* Holland Code 大字母展示 */
.code-display {
  display: flex;
  justify-content: center;
  gap: 1rem;
  margin: 2rem 0;
}

.code-letter {
  font-size: 7rem;
  font-weight: 900;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  animation: letterPop 0.5s ease-out;
  animation-fill-mode: backwards;
}

.code-letter:nth-child(1) {
  animation-delay: 0.1s;
}

.code-letter:nth-child(2) {
  animation-delay: 0.2s;
}

.code-letter:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes letterPop {
  from {
    transform: scale(0.5);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

/* 类型名称展示 */
.type-names {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  margin: 2rem 0;
  flex-wrap: wrap;
}

.type-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  color: white;
  font-weight: 600;
  font-size: 1.125rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  animation: badgeFadeIn 0.5s ease-out;
  animation-fill-mode: backwards;
}

.primary-badge {
  animation-delay: 0.4s;
}

.secondary-badge {
  animation-delay: 0.5s;
  opacity: 0.95;
  font-size: 1rem;
}

.tertiary-badge {
  animation-delay: 0.6s;
  opacity: 0.9;
  font-size: 0.95rem;
}

@keyframes badgeFadeIn {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.type-emoji {
  font-size: 1.5rem;
}

.plus-sign {
  color: rgba(255, 255, 255, 0.7);
  font-size: 1.25rem;
  font-weight: 600;
}

/* 主类型描述 */
.primary-description {
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.125rem;
  line-height: 1.6;
  max-width: 600px;
  margin: 2rem auto;
}

/* 测试信息 */
.test-info {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.875rem;
  margin-top: 2rem;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .holland-code-hero {
    padding: 2rem 1.5rem;
  }
  
  .hero-title {
    font-size: 1.5rem;
  }
  
  .code-display {
    gap: 0.5rem;
  }
  
  .code-letter {
    font-size: 4rem;
  }
  
  .type-names {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .type-badge {
    font-size: 1rem;
    padding: 0.625rem 1.25rem;
  }
  
  .secondary-badge,
  .tertiary-badge {
    font-size: 0.95rem;
  }
  
  .plus-sign {
    display: none;
  }
  
  .primary-description {
    font-size: 1rem;
  }
}
</style>

